import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Table, Form, Input, InputNumber, Select, Button, Space, Popconfirm, message, Row, Col, Tooltip } from 'antd';
import Icon, { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { ReactComponent as MeterSvg } from '../../static/icons/energy_brought.svg';
import queryApi from '../../modules/api/queryApi';
import './Dashboard.scss';

const typeOptions = [
  { value: 'grid_electric_meter', label: 'Grid Electric Meter' },
  { value: 'solar_electric_meter', label: 'Solar Electric Meter' },
];

const MeterFormModal = ({ open, onCancel, onSuccess, stationId, editingMeter }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const initialType = useMemo(() => typeOptions[0]?.value, []);

  useEffect(() => {
    if (open) {
      form.resetFields();
      if (editingMeter) {
        form.setFieldsValue({
          ...editingMeter,
          ctRatio: editingMeter.ctRatio ?? undefined,
          transformerRatedCurrent: editingMeter.transformerRatedCurrent ?? undefined,
          installedPvCapacityKwp: editingMeter.installedPvCapacityKwp ?? undefined,
        });
      } else {
        form.setFieldsValue({
          siteId: stationId,
          type: initialType,
        });
      }
    }
  }, [open, editingMeter, stationId, form, initialType]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        ...values,
        siteId: stationId,
      };

      if (editingMeter) {
        await queryApi.patch(`meter/${encodeURIComponent(editingMeter.meterId)}`, payload);
        message.success('Meter updated');
      } else {
        await queryApi.post('meter', payload);
        message.success('Meter created');
      }
      onSuccess();
    } catch (err) {
      if (err?.errorFields) return;
      const apiMessage = err?.response?.data?.message;
      message.error(apiMessage || 'Failed to save meter');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      onOk={handleSubmit}
      okText={editingMeter ? 'Update Meter' : 'Add Meter'}
      confirmLoading={saving}
      width={1000}
      className="updateSiteModal"
      wrapClassName="updateSiteModalWrap"
      centered
      destroyOnClose
      title={editingMeter ? 'Edit Meter' : 'Add New Meter'}
      zIndex={1001}
    >
      <div className="modalSection">
        <h3>{editingMeter ? 'Edit Meter Details' : 'Add New Meter'}</h3>
        <Form layout="horizontal" form={form}>
          <Row gutter={[24, 12]}>
            <Col span={12}>
              <Form.Item
                label="Meter ID"
                name="meterId"
                rules={[{ required: true, message: 'Meter ID is required' }]}
                className="formRow"
              >
                <Input placeholder="Unique meter ID" disabled={!!editingMeter} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Type"
                name="type"
                rules={[{ required: true, message: 'Type is required' }]}
                className="formRow"
              >
                <Select options={typeOptions} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Device Name" name="deviceName" className="formRow">
                <Input placeholder="Friendly name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Source" name="source" className="formRow">
                <Input placeholder="e.g. qk" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="CT Ratio" name="ctRatio" className="formRow">
                <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Rated Current (A)" name="transformerRatedCurrent" className="formRow">
                <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Installed PV Capacity (kWp)" name="installedPvCapacityKwp" className="formRow">
                <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
    </Modal>
  );
};

const MetersModal = ({ open, onClose, stationId }) => {
  const [loading, setLoading] = useState(false);
  const [meters, setMeters] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingMeter, setEditingMeter] = useState(null);

  const fetchMeters = async () => {
    if (!stationId) return;
    setLoading(true);
    try {
      const data = await queryApi.get(`meter?siteId=${encodeURIComponent(stationId)}`);
      setMeters(Array.isArray(data) ? data : []);
    } catch (err) {
      message.error('Failed to load meters');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchMeters();
    }
  }, [open, stationId]);

  const handleDelete = async (meterId) => {
    try {
      await queryApi.delete(`meter/${encodeURIComponent(meterId)}`);
      message.success('Meter deleted');
      fetchMeters();
    } catch (err) {
      message.error('Failed to delete meter');
    }
  };

  const openAddModal = () => {
    setEditingMeter(null);
    setIsEditModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditingMeter(record);
    setIsEditModalOpen(true);
  };

  const handleFormSuccess = () => {
    setIsEditModalOpen(false);
    fetchMeters();
  };

  const columns = [
    {
      title: 'Meter ID',
      dataIndex: 'meterId',
      key: 'meterId',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (val) => typeOptions.find((o) => o.value === val)?.label || val,
    },
    {
      title: 'Device Name',
      dataIndex: 'deviceName',
      key: 'deviceName',
    },
    {
      title: 'CT Ratio',
      dataIndex: 'ctRatio',
      key: 'ctRatio',
    },
    {
      title: 'Rated Current',
      dataIndex: 'transformerRatedCurrent',
      key: 'transformerRatedCurrent',
    },
    {
      title: 'Installed PV (kWp)',
      dataIndex: 'installedPvCapacityKwp',
      key: 'installedPvCapacityKwp',
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button type="text" icon={<EditOutlined />} onClick={() => openEditModal(record)} />
          </Tooltip>
          <Popconfirm
            title="Delete meter?"
            onConfirm={() => handleDelete(record.meterId)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        width={1000}
        className="updateSiteModal"
        wrapClassName="updateSiteModalWrap"
        centered
        destroyOnClose
        title={
          <Space>
            <Icon component={MeterSvg} />
            <span>Meters</span>
          </Space>
        }
      >
        <div className="modalSection">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3>Meter List</h3>
            <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}>
              Add Meter
            </Button>
          </div>
          <Table
            loading={loading}
            dataSource={meters}
            rowKey="meterId"
            columns={columns}
            pagination={{ pageSize: 6 }}
            size="middle"
          />
        </div>
      </Modal>

      <MeterFormModal
        open={isEditModalOpen}
        onCancel={() => setIsEditModalOpen(false)}
        onSuccess={handleFormSuccess}
        stationId={stationId}
        editingMeter={editingMeter}
      />
    </>
  );
};

export default MetersModal;
